import { useEffect, useState } from "react";

/**
 * Chrome/Android 계열이 설치 가능 조건을 만족하면 발생시키는 이벤트.
 * 표준 타입에 아직 없어 직접 정의한다.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/**
 * - `installable` : beforeinstallprompt 수신 → 네이티브 설치창 호출 가능 (Android/Desktop Chrome 계열)
 * - `ios`         : iOS/iPadOS — beforeinstallprompt 미지원, "공유 → 홈 화면에 추가" 안내 필요
 * - `installed`   : 이미 설치(standalone)되어 실행 중 → 배너 불필요
 * - `unsupported` : 위 어디에도 해당 없음 (설치 유도 안 함)
 */
type InstallPlatform = "installable" | "ios" | "installed" | "unsupported";

/**
 * 이벤트와 무관하게 즉시 판정 가능한 초기 플랫폼.
 * (standalone 여부 / iOS 여부 — beforeinstallprompt는 effect에서 후속 갱신)
 */
function detectInitialPlatform(): InstallPlatform {
  if (typeof window === "undefined") return "unsupported";

  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari의 비표준 standalone 플래그
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (isStandalone) return "installed";

  // iOS/iPadOS 감지 (iPadOS는 MacIntel + 터치로 위장하므로 별도 체크)
  const ua = window.navigator.userAgent.toLowerCase();
  const isIOS =
    /iphone|ipad|ipod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (isIOS) return "ios";

  return "unsupported";
}

/**
 * PWA 설치 유도용 상태/액션 훅.
 * iOS는 Web Push의 전제(홈 화면 설치)라 별도 분기로 안내한다.
 */
export function usePWAInstall(): {
  platform: InstallPlatform;
  promptInstall: () => Promise<void>;
} {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [platform, setPlatform] = useState<InstallPlatform>(detectInitialPlatform);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault(); // 브라우저 기본 미니 인포바 억제 → 커스텀 UI로 대체
      setDeferred(e as BeforeInstallPromptEvent);
      setPlatform("installable");
    };
    const onInstalled = () => {
      setPlatform("installed");
      setDeferred(null);
    };

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    // prompt는 1회용 — 결과와 무관하게 폐기 (appinstalled가 후처리)
    setDeferred(null);
  };

  return { platform, promptInstall };
}
